import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  adminCreateExternalSharesApis,
  adminExternalSharesApis,
  investorExternalSharesApis,
} from 'apis/external-share-exchange.apis';
import { showServerError, successNotify } from 'helpers';
import { GetListPayload, WithIsAdmin } from 'interfaces';
import { ExternalShareExchangePayload } from 'interfaces/external-share-exchange.interface';
import { getDeletedShareExchangesMessage } from 'utils/messages';

export const EXTERNAL_SHARE_EXCHANGE_SLICE_NAME = 'Off Platform-share-exchange';

export function createExternalShareExchangeFormData(payload: ExternalShareExchangePayload): FormData {
  const formData = new FormData();

  // 1) Append Investor ID (if not null)
  if (payload.investorId !== null) {
    formData.append('investorId', String(payload.investorId));
  }
  formData.append(`companyId`, payload.companyId);

  // 2) Append top-level scalar fields
  payload.exchanges.forEach((exchange, index) => {
    formData.append(
      `exchanges.${index}.classOfShares`,
      typeof exchange.classOfShares === 'object' ? exchange.classOfShares.value : exchange.classOfShares,
    );
    formData.append(`exchanges.${index}.noOfShares`, exchange.noOfShares);
    formData.append(`exchanges.${index}.closingDate`, exchange.closingDate);
    formData.append(`exchanges.${index}.companyPrice`, exchange.companyPrice);
    formData.append(`exchanges.${index}.fundSharePrice`, exchange.fundSharePrice);
    formData.append(`exchanges.${index}.noExchangedShares`, exchange.noExchangedShares);
    formData.append(`exchanges.${index}.originalInvestmentPrice`, exchange.originalInvestmentPrice);
  });

  // 3) Append investor object as JSON
  formData.append('investor', JSON.stringify(payload.investor));

  // 4) Append file entries
  payload.files.forEach((fileData, index) => {
    formData.append(`files.${index}.documentType`, fileData.documentType);
    formData.append(`files.${index}.documentName`, fileData.documentName);
    formData.append(`files.${index}.file`, fileData.file, fileData.file.name);
  });

  return formData;
}

export const createExternalShareExchange = createAsyncThunk(
  'share-exchanges/external',
  async (payload: ExternalShareExchangePayload, { rejectWithValue }) => {
    try {
      const formData = createExternalShareExchangeFormData(payload);

      const response = await adminCreateExternalSharesApis.createExternalShareExchange(formData);

      successNotify('Off Platform Share Exchange created successfully');
      return response.data;
    } catch (err) {
      showServerError(err);
      return rejectWithValue(err);
    }
  },
);

export const getExternalShareExchanges = createAsyncThunk(
  `${EXTERNAL_SHARE_EXCHANGE_SLICE_NAME}/getExternalShareExchanges`,
  async (data: GetListPayload) => {
    const response = await investorExternalSharesApis.getExternalShareExchanges(data);
    return response.data;
  },
);

export const getExternalShareExchange = createAsyncThunk(
  `${EXTERNAL_SHARE_EXCHANGE_SLICE_NAME}/getExternalShareExchange`,
  async ({ id, isAdmin }: { id: string; isAdmin?: boolean }, { rejectWithValue }) => {
    try {
      const response = await (isAdmin
        ? adminExternalSharesApis.getExternalShareExchange(id)
        : investorExternalSharesApis.getExternalShareExchange(id));

      return response.data;
    } catch (err) {
      showServerError(err);

      return rejectWithValue(err);
    }
  },
);

export const deleteExternalShareExchanges = createAsyncThunk(
  `${EXTERNAL_SHARE_EXCHANGE_SLICE_NAME}/deleteExternalShareExchanges`,
  async ({ ids, isAdmin }: WithIsAdmin<{ ids: number[] }>, { rejectWithValue }) => {
    try {
      await Promise.all(
        ids.map((dealId) =>
          isAdmin
            ? adminExternalSharesApis.deleteExternalShareExchange(dealId)
            : investorExternalSharesApis.deleteExternalShareExchange(dealId),
        ),
      );

      successNotify(getDeletedShareExchangesMessage(ids?.length));
      return null;
    } catch (e) {
      showServerError(e);
      return rejectWithValue(e);
    }
  },
);

export const deleteExternalShareExchange = createAsyncThunk(
  `${EXTERNAL_SHARE_EXCHANGE_SLICE_NAME}/deleteExternalShareExchange`,
  async ({ id, isAdmin }: WithIsAdmin<{ id: number }>, { rejectWithValue }) => {
    try {
      const response = await (isAdmin
        ? adminExternalSharesApis.deleteExternalShareExchange(id)
        : investorExternalSharesApis.deleteExternalShareExchange(id));

      successNotify('The deal was successfully deleted');
      return response.data;
    } catch (e) {
      showServerError(e);
      return rejectWithValue(e);
    }
  },
);
